/**
 * Appearance preference: cream, dark, or whatever the device is doing.
 *
 * Kept out of React state deliberately. The attribute has to be on <html> before
 * the first paint or the page flashes the wrong palette, and only an inline
 * script in index.html can run that early, so the DOM attribute is the source of
 * truth and React reads from it rather than owning it.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/* Also read by the inline script in index.html. Changing it here without
   changing it there means the flash comes back. */
export const THEME_STORAGE_KEY = 'alohamula.theme';

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function isPreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && PREFERENCES.includes(value as ThemePreference);
}

function darkMedia(): MediaQueryList {
  return window.matchMedia('(prefers-color-scheme: dark)');
}

/**
 * Falls back to 'system' rather than throwing. Private browsing and blocked
 * storage both make localStorage access raise, and an unreadable preference is
 * not a reason to fail to render a budget.
 */
export function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  return darkMedia().matches ? 'dark' : 'light';
}

export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;

  /*
   * The browser paints its own surfaces — the URL bar on iOS, the overscroll
   * gutter — from theme-color, so it has to move with the palette or the chrome
   * stays cream above a dark page.
   */
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#1c1a17' : '#f6f1e8');
  }

  return resolved;
}

export function writePreference(preference: ThemePreference): ResolvedTheme {
  try {
    if (preference === 'system') {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    /* Preference won't survive the reload; the palette still changes now. */
  }
  return applyTheme(preference);
}

/**
 * Notifies when the device flips appearance, so a window left on "System"
 * follows along instead of waiting for a reload.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const media = darkMedia();
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
